import React, { useEffect, useMemo, useState } from "react"
import TableContainer from "components/Common/TableContainer"
import { getTableSortingHeaderIcon } from "helpers/common"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"
import { useLazyGetProfileUpdateRequestsQuery } from "store/api/profile/profile.api"
import {
  PopoverBody,
  PopoverHeader,
  UncontrolledPopover,
  UncontrolledTooltip,
} from "reactstrap"
import { Link } from "react-router-dom"
import eye from "../../../assets/images/icons/eye.svg"
import { usePermission } from "hooks/usePermission"
import { PERMISSIONS } from "helpers/roles"
import { useSelector } from "react-redux"
import OutsideClickPopover from "modals/OutsideClickPopover"

const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
  timestamp: "",
}

const InvestorsProfileTable = ({ type }) => {
  const [getResults, { isFetching }] = useLazyGetProfileUpdateRequestsQuery()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const { hasPermission } = usePermission()
  const { userType } = useSelector((state) => state.user)

  //meta title
  document.title = "Profile Update Requests | Prachay"

  useEffect(() => {
    getList()
  }, [filters, type])

  const getList = async () => {
    try {
      const params = { ...filters }
      if (type) params["type"] = type
      const { data } = await getResults(params).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  const getStatus = (key) => {
    let status = "Active"
    let className = "Active"

    switch (key) {
      case "PENDING":
        status = "Pending"
        className = "kyc_pending"
        break

      case "SUBMITTED":
        status = "Under Review"
        className = "under_review"
        break

      case "REJECTED":
        status = "Rejected"
        className = "rejected"
        break

      default:
        status = "Approved"
        className = "approved"
        break
    }

    return (
      <div className={`badge ${className}`}>
        <span></span> {status}
      </div>
    )
  }

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Date"),
        accessorKey: "formatted_created_at",
        enableColumnFilter: false,
        enableSorting: true,
      },
      ...(userType === "ADMIN"
        ? [
            {
              accessorKey: "name",
              enableColumnFilter: false,
              enableSorting: true,
              header: ({ column }) => getTableSortingHeaderIcon(column, "Name"),
              cell: ({ row }) => (
                <DefaultAvatar
                  avatar_url={
                    row?.original.investor?.avatar_url ||
                    row?.original.wealthPartner?.avatar_url
                  }
                  email={
                    row?.original.investor?.email ||
                    row?.original.wealthPartner?.email
                  }
                  name={
                    row?.original.investor?.name ||
                    row?.original.wealthPartner?.name
                  }
                />
              ),
            },
            {
              header: "Code",
              accessorKey: "unique_code",
              enableColumnFilter: false,
              enableSorting: false,
              cell: ({ row }) => (
                <div className="code-status">
                  {row?.original.investor?.unique_code ||
                    row?.original.wealthPartner?.unique_code}
                </div>
              ),
            },
          ]
        : []),
      {
        header: ({ column }) =>
          getTableSortingHeaderIcon(column, "Changes Requested"),
        accessorKey: "steps",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <>{row?.original?.initial_steps.join(", ")}</>,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="code-status">
            {getStatus(row?.original?.status || row?.original?.status)}
          </div>
        ),
      },
    ]

    const canSeeActions =
      hasPermission(
        type === "INVESTOR"
          ? PERMISSIONS.INVESTOR_VIEW
          : PERMISSIONS.WEALTH_PARTNER_VIEW
      ) ||
      hasPermission(
        type === "INVESTOR"
          ? PERMISSIONS.INVESTOR_UPDATE
          : PERMISSIONS.WEALTH_PARTNER_UPDATE
      )

    if (canSeeActions) {
      baseColumns.push({
        id: "action",
        header: "Actions",
        cell: ({ row }) => {
          const viewTooltip = `editTooltip-${row?.original.id}`
          const rejectedTooltip = `rejectedTooltip-${row?.original?.id}`

          const actionUrl =
            userType === "ADMIN"
              ? `/${
                  type === "INVESTOR" ? "investors" : "wealth-partners"
                }/profile-updates/view/${row?.original.id}`
              : `/profile/updates/view/${row?.original.id}`

          return (
            <div className="action-view d-flex justify-content-center align-items-center gap-3">
              <Link to={actionUrl} id={viewTooltip}>
                <img src={eye} alt="" />
                <UncontrolledTooltip placement="top" target={viewTooltip}>
                  View
                </UncontrolledTooltip>
              </Link>
              {row?.original?.rejection_message && (
                <Link to={"#"} id={rejectedTooltip}>
                  <i
                    className="mdi mdi-information-outline"
                    style={{ color: "#7a7a7a", fontSize: "16px" }}
                  />
                  <OutsideClickPopover
                    target={rejectedTooltip}
                    title="Reason for Rejection"
                    body={row?.original?.rejection_message}
                  />
                </Link>
              )}
            </div>
          )
        },
      })
    }

    return baseColumns
  }, [type, userType, hasPermission])

  return (
    <>
      <TableContainer
        isLoading={isFetching}
        count={count}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        data={data}
        isGlobalFilter={true}
        isCustomPageSize={true}
        // isGlobalFilter={true}
        isPagination={true}
        pagination="pagination"
        paginationWrapper="dataTables_paginate paging_simple_numbers"
        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
      />
    </>
  )
}

export default InvestorsProfileTable
